import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    CustomAPIResponse,
    ENVIRONMENT,
    normalizeAccountProfile,
    normalizeAccountType,
    PaymentActivityRequest,
    PaymentActivityResponse,
    PaymentHistoryResponse,
    PaymentProcessingStatus,
    PaymentsByStatusResponse,
    PaymentSummaryRequest,
    PaymentSummaryResponse,
    TaxAndInsuranceDetails,
    TaxAndInsuranceRequest,
} from '@ld.consumer-portal/models';
import { CustomErrorHandlerService, LoggingService} from '@ld.consumer-portal/utils';
import { firstValueFrom, retry, timer } from 'rxjs';
import type {
    AddBankAccountRequest,
    AddBankAccountResponse,
    AutopayCancelRequest,
    AutopayPaymentRecord,
    AutopaySetupRequest,
    AutopaySetupResponse,
    AutopayUpdateRequest,
    DeletedPaymentRecord,
    PaymentConfirmation,
    PaymentDeleteRequest,
    PaymentDueSelection,
    PaymentFeeSelection,
    PaymentPageData,
    PaymentProfile,
    PaymentSubmitRequest,
    PaymentUpdateRequest,
    PendingPayment,
    RoutingNumberValidation,
} from './payment.models';

export const TOTAL_DRAFT_AMOUNT_MISMATCH_ERROR = 'TOTAL_DRAFT_AMOUNT_MISMATCH';

const PAYMENT_RETRY_CONFIG = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
};

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly customErrorHandler = inject(CustomErrorHandlerService);
  private readonly loggingService = inject(LoggingService);

  private get baseUrl(): string {
    return `${this.env.appSettings.ServicingLINCPortalPayments}/${this.env.appSettings.apiNamespace}/ServicingLINCPortalIntegration/Payments`;
  }

  private get profileBaseUrl(): string {
    return `${this.env.appSettings.ServicingLINCPortalPayments}/${this.env.appSettings.apiNamespace}/ServicingLINCPortalIntegration/PaymentProfile`;
  }

  private retryWithBackoff<T>(context: string) {
    return retry<T>({
      count: PAYMENT_RETRY_CONFIG.maxRetries,
      delay: (error: unknown, retryCount: number) => {
        const delayMs = Math.min(
          PAYMENT_RETRY_CONFIG.initialDelayMs * Math.pow(2, retryCount - 1),
          PAYMENT_RETRY_CONFIG.maxDelayMs
        );
        this.loggingService.logWarning(
          `${context} failed, retrying (${retryCount}/${PAYMENT_RETRY_CONFIG.maxRetries})`,
          {
            retryCount,
            delayMs,
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
          }
        );
        return timer(delayMs);
      },
    });
  }

  async getPaymentPageData(loanNumber: string, encryptionValue: string): Promise<PaymentPageData> {
    const url = `${this.baseUrl}/${loanNumber}`;
    const params = { encryptionValue };

    const response = await firstValueFrom(
      this.http.get<PaymentApiResponse>(url, { params }).pipe(this.retryWithBackoff('Payment page data request'))
    );

    return this.transformToPaymentPageData(response, loanNumber);
  }

  async getPaymentHistory(loanNumber: string, encryptionValue: string): Promise<PaymentHistoryResponse> {
    const url = `${this.baseUrl}/${loanNumber}?encryptionValue=${encodeURIComponent(encryptionValue)}`;

    return firstValueFrom(
      this.http.get<PaymentHistoryResponse>(url).pipe(this.retryWithBackoff('Payment history request'))
    );
  }

  async getPaymentProfiles(
    borrowerId: string,
    loanNumber: string,
    encryptionValue: string,
    firstName?: string,
    lastName?: string,
    loanType?: string,
  ): Promise<PaymentProfile[]> {
    const url = `${this.profileBaseUrl}/${borrowerId}`;
    const params: Record<string, string> = { loanNumber, encryptionValue };
    if (firstName) params['firstName'] = firstName;
    if (lastName) params['lastName'] = lastName;
    if (loanType) params['loanType'] = loanType;

    const response = await firstValueFrom(this.http.get<PaymentProfileApiResponse[]>(url, { params }));

    return response.map(this.transformToPaymentProfile);
  }

  async submitPayment(request: PaymentSubmitRequest): Promise<PaymentConfirmation> {
    const url = `${this.baseUrl}/${request.loanNumber}?encryptionValue=${encodeURIComponent(request.encryptionValue)}`;

    const effectiveDues = request.paymentDue.filter((p: PaymentDueSelection) => p.isSelected);

    const monthlyPaymentAmount = Math.round((effectiveDues[0]?.totalMonthlyPayment ?? 0) * 100) / 100;

    const selectedFees =
      request.fees
        ?.filter((f: PaymentFeeSelection) => f.isSelected)
        .map((f) => {
          const numericType = this.feeTypeToNumber(f.type);
          return {
            type: numericType,
            code: this.feeTypeToCode(f.type),
            description: f.description ?? '',
            amount: f.selectedAmount ?? f.amount,
            breakdown: f.breakdown ?? null,
          };
        }) ?? [];

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const paymentDuePayload = effectiveDues.map(({ isSelected, ...due }) => due);

    const additionalPrincipal = request.additionalPrincipal ?? 0;
    const additionalEscrow = request.additionalEscrow ?? 0;
    const totalDraftAmount = Math.round((request.totalAmount ?? 0) * 100) / 100;

    const body = {
      encryptionValue: request.encryptionValue,
      paymentProfileId: request.paymentProfileId,
      paymentDate: request.paymentDate,
      paymentDue: paymentDuePayload,
      additionalPrincipal,
      additionalEscrow,
      fees: selectedFees,
      loanType: request.loanType ?? 0,
      totalDraftAmount,
      monthlyPaymentAmount,
      userId: request.userId,
      authorizedBy: request.authorizedBy,
      userName: request.userName,
      userEmail: request.userEmail,
      channelId: request.channelId ?? '2', // Default to web channel
      badCheckStopCode: request.badCheckStopCode,
      pifStopCode: request.pifStopCode,
      foreclosureStopCode: request.foreclosureStopCode,
      processStopCode: request.processStopCode,
      isPaymentInSuspense: request.isPaymentInSuspense,
      nextPaymentDueDate: request.nextPaymentDueDate,
      loanStatus: request.loanStatus,
      isAutopayScheduled: request.isAutopayScheduled,
      fhaPaymentSupplement: request.fhaPaymentSupplement,
      njFireFeeAmount: request.njFireFeeAmount,
      delinquency: request.delinquency,
      sendEmailConfirmation: request.sendEmailConfirmation ?? true,
    };

    try {
      const response = await firstValueFrom(this.http.post<PaymentConfirmationResponse>(url, body));

      const isSuccess = !!response.confirmationNumber;

      return {
        confirmationNumber: response.confirmationNumber,
        paymentId: response.paymentId,
        paymentDate: response.paymentDate,
        amount: response.amount,
        isSuccess,
        errorMessage: response.errorMessage,
      };
    } catch (error: unknown) {
      const customApiResponse = this.customErrorHandler.extractCustomApiResponse(error);
      if (customApiResponse?.resultCode && this.customErrorHandler.isCustomError(url, customApiResponse)) {
        return {
          confirmationNumber: '',
          paymentId: '',
          paymentDate: '',
          amount: 0,
          isSuccess: false,
          errorMessage: '', // Custom error modal handles the message display
        };
      }

      const errorMessage = this.extractApiErrorMessage(error);
      return {
        confirmationNumber: '',
        paymentId: '',
        paymentDate: '',
        amount: 0,
        isSuccess: false,
        errorMessage,
      };
    }
  }

  private isCustomError(error: unknown): boolean {
    if (error && typeof error === 'object' && 'error' in error) {
      const httpError = error as { error?: CustomAPIResponse };
      return !!httpError.error?.resultCode;
    }
    return false;
  }

  private extractCustomErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'error' in error) {
      const httpError = error as { error?: CustomAPIResponse };
      const resultCode = httpError.error?.resultCode;
      if (resultCode) {
        const customError = this.customErrorHandler.getCustomErrorObj(resultCode);
        if (customError) {
          return customError.errorMessage;
        }
      }
    }
    return 'An error occurred while processing your payment.';
  }

  private extractApiErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'error' in error) {
      const httpError = error as { error?: { messages?: string[]; errorMessage?: string; resultCode?: string } };
      const errorBody = httpError.error;

      if (errorBody) {
        if (errorBody.resultCode) {
          const customError = this.customErrorHandler.getCustomErrorObj(errorBody.resultCode);
          if (customError) {
            return customError.errorMessage;
          }
        }

        if (Array.isArray(errorBody.messages) && errorBody.messages.length > 0) {
          const joined = errorBody.messages.join('. ');
          if (this.isTotalDraftAmountMismatch(joined)) {
            return TOTAL_DRAFT_AMOUNT_MISMATCH_ERROR;
          }
          return joined;
        }
        if (errorBody.errorMessage) {
          if (this.isTotalDraftAmountMismatch(errorBody.errorMessage)) {
            return TOTAL_DRAFT_AMOUNT_MISMATCH_ERROR;
          }
          return errorBody.errorMessage;
        }
      }
    }

    if (error && typeof error === 'object' && 'message' in error) {
      return (error as { message: string }).message;
    }

    return 'An error occurred while processing your payment. Please try again.';
  }

  private isTotalDraftAmountMismatch(message: string): boolean {
    const lower = message.toLowerCase();
    return lower.includes('totaldraftamount') && lower.includes('not matching');
  }

  async addPaymentProfile(
    borrowerId: string,
    loanNumber: string,
    encryptionValue: string,
    profile: Partial<PaymentProfile>,
  ): Promise<PaymentProfile> {
    const url = `${this.profileBaseUrl}/${borrowerId}`;
    const params = { encryptionValue, loanNumber };

    const response = await firstValueFrom(this.http.post<PaymentProfileApiResponse>(url, profile, { params }));

    return this.transformToPaymentProfile(response);
  }

  async deletePaymentProfile(
    borrowerId: string,
    profileId: string,
    userId: string,
    loanNumber: string,
    encryptionValue: string,
  ): Promise<{ success: boolean; errorMessage?: string }> {
    const url = `${this.profileBaseUrl}/${borrowerId}/${profileId}`;
    const params = { userId, loanNumber, encryptionValue };

    try {
      await firstValueFrom(this.http.delete(url, { params }));
      return { success: true };
    } catch (error: unknown) {
      if (this.customErrorHandler.processHttpError(url, error)) {
        return {
          success: false,
          errorMessage: this.extractCustomErrorMessage(error),
        };
      }

      const err = error as { error?: { message?: string } };
      return {
        success: false,
        errorMessage: err?.error?.message ?? 'Failed to delete bank account',
      };
    }
  }

  async getPendingPaymentById(loanNumber: string, paymentId: string, encryptionValue: string): Promise<PendingPayment> {
    const url = `${this.baseUrl}/${loanNumber}/Pending/${paymentId}`;
    const params = { encryptionValue };

    const response = await firstValueFrom(
      this.http.get<PendingPaymentApiResponse>(url, { params }).pipe(this.retryWithBackoff('Pending payment request'))
    );

    return this.transformToPendingPayment(response, paymentId);
  }

  async updatePayment(request: PaymentUpdateRequest): Promise<PaymentConfirmation> {
    const url = `${this.baseUrl}/${request.loanNumber}/${request.paymentId}?encryptionValue=${encodeURIComponent(request.encryptionValue)}`;

    const effectiveDues = request.paymentDue.filter((p: PaymentDueSelection) => p.isSelected);

    const monthlyPaymentAmount = Math.round((effectiveDues[0]?.totalMonthlyPayment ?? 0) * 100) / 100;

    const selectedFees =
      request.fees
        ?.filter((f: PaymentFeeSelection) => f.isSelected)
        .map((f) => {
          const numericType = this.feeTypeToNumber(f.type);
          return {
            type: numericType,
            code: this.feeTypeToCode(f.type),
            description: f.description ?? '',
            amount: f.selectedAmount ?? f.amount,
            breakdown: f.breakdown ?? null,
          };
        }) ?? [];

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const paymentDuePayload = effectiveDues.map(({ isSelected, ...due }) => due);

    const additionalPrincipal = request.additionalPrincipal ?? 0;
    const additionalEscrow = request.additionalEscrow ?? 0;
    const totalDraftAmount = Math.round((request.totalAmount ?? 0) * 100) / 100;

    const body = {
      encryptionValue: request.encryptionValue,
      paymentProfileId: request.paymentProfileId,
      paymentDate: request.paymentDate,
      originalPaymentDate: request.originalPaymentDate,
      paymentDue: paymentDuePayload,
      additionalPrincipal,
      additionalEscrow,
      fees: selectedFees,
      loanType: request.loanType ?? 0,
      totalDraftAmount,
      monthlyPaymentAmount,
      userId: request.userId,
      authorizedBy: request.authorizedBy,
      userName: request.userName,
      userEmail: request.userEmail,
      channelId: request.channelId ?? '2',
      badCheckStopCode: request.badCheckStopCode,
      pifStopCode: request.pifStopCode,
      foreclosureStopCode: request.foreclosureStopCode,
      processStopCode: request.processStopCode,
      isPaymentInSuspense: request.isPaymentInSuspense,
      nextPaymentDueDate: request.nextPaymentDueDate,
      loanStatus: request.loanStatus,
      isAutopayScheduled: request.isAutopayScheduled,
      fhaPaymentSupplement: request.fhaPaymentSupplement,
      njFireFeeAmount: request.njFireFeeAmount,
      delinquency: request.delinquency,
      sendEmailConfirmation: request.sendEmailConfirmation ?? true,
    };

    try {
      const response = await firstValueFrom(this.http.put<PaymentConfirmationResponse>(url, body));

      const isSuccess = !!response.confirmationNumber;

      return {
        confirmationNumber: response.confirmationNumber,
        paymentId: response.paymentId,
        paymentDate: response.paymentDate,
        amount: response.amount,
        isSuccess,
        errorMessage: response.errorMessage,
      };
    } catch (error: unknown) {
      const customApiResponse = this.customErrorHandler.extractCustomApiResponse(error);
      if (customApiResponse?.resultCode && this.customErrorHandler.isCustomError(url, customApiResponse)) {
        return {
          confirmationNumber: '',
          paymentId: '',
          paymentDate: '',
          amount: 0,
          isSuccess: false,
          errorMessage: '',
        };
      }

      const errorMessage = this.extractApiErrorMessage(error);
      return {
        confirmationNumber: '',
        paymentId: '',
        paymentDate: '',
        amount: 0,
        isSuccess: false,
        errorMessage,
      };
    }
  }

  async deletePayment(request: PaymentDeleteRequest): Promise<void> {
    const url = `${this.baseUrl}/${request.loanNumber}/${request.paymentId}`;
    const params = {
      userId: request.userId,
      authorizedBy: request.authorizedBy,
    };

    const body = {
      deletedReason: request.deletedReason,
      loanType: request.loanType ?? 0,
    };

    await firstValueFrom(this.http.delete(url, { params, body }));
  }

  async getBankAccounts(
    borrowerId: string,
    loanNumber: string,
    encryptionValue: string,
    firstName?: string,
    lastName?: string,
    loanType?: string,
  ): Promise<PaymentProfile[]> {
    const url = `${this.profileBaseUrl}/${borrowerId}`;
    const params: Record<string, string> = { loanNumber, encryptionValue };
    if (firstName) params['firstName'] = firstName;
    if (lastName) params['lastName'] = lastName;
    if (loanType) params['loanType'] = loanType;

    const response = await firstValueFrom(this.http.get<PaymentProfileApiResponse[]>(url, { params }));

    return response.map(this.transformToPaymentProfile);
  }

  async addBankAccount(
    borrowerId: string,
    request: AddBankAccountRequest,
    loanNumber: string,
    encryptionValue: string,
  ): Promise<AddBankAccountResponse> {
    const url = `${this.profileBaseUrl}/${borrowerId}`;
    const params = { encryptionValue, loanNumber };

    try {
      const response = await firstValueFrom(this.http.post<AddBankAccountApiResponse>(url, request, { params }));

      const normalizedStatus = this.normalizeVerificationStatus(response.accountVerificationStatus);

      const result: AddBankAccountResponse = {
        paymentProfileId: response.paymentProfileId,
        accountVerificationStatus: normalizedStatus,
        accountValidationResult: response.accountValidationResult,
        errorMessage: response.errorMessage,
      };

      if (response.accountVerificationStatus?.toLowerCase() === 'valid') {
        result.profile = {
          profileId: response.paymentProfileId,
          lastFourAccountNumber: request.paymentProfileDetails.accountNumber.slice(-4),
          accountType: request.paymentProfileDetails.accountType,
          accountProfile: request.paymentProfileDetails.accountProfile,
          bankName: response.bankName,
          isDefault: request.defaultPaymentProfile,
          firstName: request.paymentProfileDetails.firstName,
          lastName: request.paymentProfileDetails.lastName,
          businessAccountName: request.paymentProfileDetails.businessAccountName,
          nickName: request.profileName,
        };
      }

      return result;
    } catch (error: unknown) {
      const httpErr = error as { error?: { accountValidationResult?: { valid: boolean; validationResultMessage?: string }; message?: string } };
      if (httpErr?.error?.accountValidationResult) {
        return {
          paymentProfileId: '',
          accountVerificationStatus: 'Invalid',
          accountValidationResult: httpErr.error.accountValidationResult,
          errorMessage: httpErr.error.accountValidationResult.validationResultMessage,
        };
      }

      if (this.customErrorHandler.processHttpError(url, error)) {
        return {
          paymentProfileId: '',
          accountVerificationStatus: 'Error',
          errorMessage: this.extractCustomErrorMessage(error),
        };
      }

      return {
        paymentProfileId: '',
        accountVerificationStatus: 'Error',
        errorMessage: httpErr?.error?.message ?? 'Failed to add bank account',
      };
    }
  }

  async setDefaultPaymentProfile(
    profile: PaymentProfile,
    loanNumber: string,
    encryptionValue: string,
  ): Promise<{ success: boolean; errorMessage?: string }> {
    const url = `${this.profileBaseUrl}/${profile.profileId}`;
    const params = { loanNumber, encryptionValue };
    const body = {
      defaultPaymentProfile: true,
      paymentProfileDetails: {
        firstName: profile.firstName ?? '',
        lastName: profile.lastName ?? '',
        businessAccountName: profile.businessAccountName ?? '',
        accountProfile: profile.accountProfile ?? 'Personal',
        accountType: profile.accountType ?? 'Checking',
        bankName: profile.bankName ?? '',
      },
    };

    try {
      await firstValueFrom(this.http.put(url, body, { params }));
      return { success: true };
    } catch (error: unknown) {
      if (this.customErrorHandler.processHttpError(url, error)) {
        return {
          success: false,
          errorMessage: this.extractCustomErrorMessage(error),
        };
      }

      const err = error as { error?: { message?: string } };
      return {
        success: false,
        errorMessage: err?.error?.message ?? 'Failed to set default account',
      };
    }
  }

  async validateRoutingNumber(
    routingNumber: string,
    loanNumber: string,
    encryptionValue: string,
  ): Promise<RoutingNumberValidation> {
    const url = `${this.profileBaseUrl}/BankValidation/${routingNumber}`;
    const params = { loanNumber, encryptionValue };

    const response = await firstValueFrom(this.http.get<RoutingNumberApiResponse>(url, { params }));

    return {
      isValid: response.valid == 1,
      routingNumber: response.routingNumber,
      bankName: response.bankName,
      errorMessage: response.error,
    };
  }

  async getAutopayPayments(loanNumber: string, encryptionValue: string): Promise<AutopayPaymentRecord[]> {
    const url = `${this.baseUrl}/${loanNumber}/Pending`;
    const params = { encryptionValue };

    const response = await firstValueFrom(
      this.http.get<PendingPaymentApiResponse[]>(url, { params }).pipe(this.retryWithBackoff('Autopay payments request'))
    );

    return response.filter((p) => (p.paymentFrequency ?? 0) > 0).map((p) => this.transformToAutopayRecord(p));
  }

  async setupAutopay(request: AutopaySetupRequest): Promise<AutopaySetupResponse> {
    const url = `${this.baseUrl}/${request.loanNumber}?encryptionValue=${encodeURIComponent(request.encryptionValue)}`;

    const body = {
      encryptionValue: request.encryptionValue,
      paymentProfileId: request.paymentProfileId,
      paymentDate: request.paymentDate,
      paymentFrequency: request.paymentFrequency,
      recPaymentStartDate: request.recPaymentStartDate,
      totalDraftAmount: request.totalDraftAmount,
      monthlyPaymentAmount: request.monthlyPaymentAmount,
      additionalPrincipal: request.additionalPrincipal ?? 0,
      semiMonthlyAdditionalPrincipal: request.semiMonthlyAdditionalPrincipal ?? 0,
      additionalEscrow: request.additionalEscrow ?? 0,
      userId: request.userId,
      userEmail: request.userEmail,
      authorizedBy: request.authorizedBy,
      channelId: request.channelId,
      userName: request.userName,
      nextPaymentDueDate: request.nextPaymentDueDate,
      isAutopayScheduled: request.isAutopayScheduled,
      loanStatus: request.loanStatus,
      badCheckStopCode: request.badCheckStopCode,
      pifStopCode: request.pifStopCode,
      foreclosureStopCode: request.foreclosureStopCode,
      processStopCode: request.processStopCode,
      isPaymentInSuspense: request.isPaymentInSuspense,
      fhaPaymentSupplement: request.fhaPaymentSupplement,
      njFireFeeAmount: request.njFireFeeAmount,
      loanType: request.loanType ?? 0,
    };

    try {
      const response = await firstValueFrom(this.http.post<AutopayApiResponse>(url, body));

      return {
        paymentId: response.paymentId,
        confirmationNumber: response.confirmationNumber,
        isSuccess: true,
      };
    } catch (error: unknown) {
      if (this.customErrorHandler.processHttpError(url, error)) {
        return {
          paymentId: '',
          confirmationNumber: '',
          isSuccess: false,
          errorMessage: this.extractCustomErrorMessage(error),
        };
      }

      const err = error as { error?: { message?: string } };
      return {
        paymentId: '',
        confirmationNumber: '',
        isSuccess: false,
        errorMessage: err?.error?.message ?? 'Failed to set up autopay',
      };
    }
  }

  async updateAutopay(request: AutopayUpdateRequest): Promise<AutopaySetupResponse> {
    const url = `${this.baseUrl}/${request.loanNumber}/${request.paymentId}?encryptionValue=${encodeURIComponent(request.encryptionValue)}`;

    const body = {
      encryptionValue: request.encryptionValue,
      paymentProfileId: request.paymentProfileId,
      paymentDate: request.paymentDate,
      paymentFrequency: request.paymentFrequency,
      recPaymentStartDate: request.recPaymentStartDate,
      totalDraftAmount: request.totalDraftAmount,
      monthlyPaymentAmount: request.monthlyPaymentAmount,
      additionalPrincipal: request.additionalPrincipal ?? 0,
      semiMonthlyAdditionalPrincipal: request.semiMonthlyAdditionalPrincipal ?? 0,
      additionalEscrow: request.additionalEscrow ?? 0,
      userId: request.userId,
      userEmail: request.userEmail,
      authorizedBy: request.authorizedBy,
      channelId: request.channelId,
      userName: request.userName,
      nextPaymentDueDate: request.nextPaymentDueDate,
      isAutopayScheduled: request.isAutopayScheduled,
      loanStatus: request.loanStatus,
      badCheckStopCode: request.badCheckStopCode,
      pifStopCode: request.pifStopCode,
      foreclosureStopCode: request.foreclosureStopCode,
      processStopCode: request.processStopCode,
      isPaymentInSuspense: request.isPaymentInSuspense,
      fhaPaymentSupplement: request.fhaPaymentSupplement,
      njFireFeeAmount: request.njFireFeeAmount,
      paymentCancellationCutoffDate: request.paymentCancellationCutoffDate,
      originalPaymentDate: request.originalPaymentDate,
      loanType: request.loanType ?? 0,
    };

    try {
      const response = await firstValueFrom(this.http.put<AutopayApiResponse>(url, body));

      return {
        paymentId: response.paymentId,
        confirmationNumber: response.confirmationNumber,
        isSuccess: true,
      };
    } catch (error: unknown) {
      if (this.customErrorHandler.processHttpError(url, error)) {
        return {
          paymentId: '',
          confirmationNumber: '',
          isSuccess: false,
          errorMessage: this.extractCustomErrorMessage(error),
        };
      }

      const err = error as { error?: { message?: string } };
      return {
        paymentId: '',
        confirmationNumber: '',
        isSuccess: false,
        errorMessage: err?.error?.message ?? 'Failed to update autopay',
      };
    }
  }

  async cancelAutopay(request: AutopayCancelRequest): Promise<{ isSuccess: boolean; errorMessage?: string }> {
    const url = `${this.baseUrl}/${request.loanNumber}/${request.paymentId}`;
    const params = {
      encryptionValue: request.encryptionValue,
    };

    const body: Record<string, unknown> = {
      userId: request.userId,
      authorizedBy: request.authorizedBy,
      deletedReason: request.deletedReason,
      loanType: request.loanType ?? 0,
      paymentCancellationCutoffDate: request.paymentCancellationCutoffDate ?? '',
    };
    if (request.userName) body['userName'] = request.userName;

    try {
      await firstValueFrom(this.http.delete(url, { params, body }));
      return { isSuccess: true };
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      return {
        isSuccess: false,
        errorMessage: err?.error?.message ?? 'Failed to cancel autopay',
      };
    }
  }

  async getDeletedPayments(loanNumber: string, encryptionValue: string): Promise<DeletedPaymentRecord[]> {
    const PAYMENT_STATUS_DELETE = PaymentProcessingStatus.Delete;
    const url = `${this.baseUrl}/${loanNumber}/PaymentStatus/${PAYMENT_STATUS_DELETE}`;
    const params = { encryptionValue };

    try {
      const response = await firstValueFrom(
        this.http.get<DeletedPaymentApiResponse[]>(url, { params }).pipe(this.retryWithBackoff('Deleted payments request'))
      );

      return response.map((p) => ({
        paymentId: p.paymentId,
        loanNumber: p.loanNumber ?? loanNumber,
        paymentFrequency: (p.paymentFrequency ?? 0) as 0 | 1 | 2,
        paymentStatus: p.paymentStatus ?? 'Delete',
        deletedAt: p.updatedAt ?? p.deletedAt ?? '',
        deletedReason: p.deletedReason,
      }));
    } catch (error) {
      this.loggingService.logException(error, 'Error fetching deleted payments:');
      return [];
    }
  }

  async getLastDeletedAutopayDate(loanNumber: string, encryptionValue: string): Promise<string | null> {
    const deletedPayments = await this.getDeletedPayments(loanNumber, encryptionValue);

    const deletedAutopays = deletedPayments
      .filter((p) => p.paymentFrequency > 0)
      .sort((a, b) => (a.deletedAt < b.deletedAt ? 1 : -1));

    return deletedAutopays[0]?.deletedAt ?? null;
  }

  private transformToPaymentPageData(response: PaymentApiResponse, loanNumber: string): PaymentPageData {
    const paymentDues: PaymentDueSelection[] = (response.paymentsDue || []).map((due) => ({
      dueDate: due.dueDate,
      totalMonthlyPayment: due.totalMonthlyPayment,
      principalAndInterest: due.principalAndInterest,
      taxAndInsurance: due.taxAndInsurance,
      isSelected: false,
      isSuspense: due.isSuspense,
    }));

    const fees: PaymentFeeSelection[] = (response.fees || []).map((fee) => ({
      type: this.toPaymentFeeType(fee.type),
      amount: fee.amount,
      description: fee.description,
      isSelected: false,
      breakdown: fee.breakdown ?? null,
    }));

    const profiles: PaymentProfile[] = (response.profiles || []).map(this.transformToPaymentProfile);

    const totalPaymentDue = paymentDues.reduce((sum, p) => sum + p.totalMonthlyPayment, 0);
    const totalPrincipalAndInterest = paymentDues.reduce((sum, p) => sum + p.principalAndInterest, 0);
    const totalTaxAndInsurance = paymentDues.reduce((sum, p) => sum + p.taxAndInsurance, 0);
    const totalFees = fees.reduce((sum, f) => sum + f.amount, 0);

    return {
      loanNumber,
      nextDueDate: new Date(response.nextDueDate),
      paymentsMadeThisMonth: response.paymentsMadeThisMonth ?? false,
      paymentBreakdown: {
        total: totalPaymentDue + totalFees,
        principalAndInterest: totalPrincipalAndInterest,
        taxesAndInsurance: totalTaxAndInsurance,
        fees: totalFees,
      },
      unpaidBalance: {
        total: response.unpaidPrincipal ?? 0,
        principalPaid: response.principalPaid ?? 0,
        escrowBalance: response.escrowBalance ?? 0,
      },
      paymentDues,
      fees,
      paymentProfiles: profiles,
      minPaymentDate: new Date(response.minPayOnDate),
      maxPaymentDate: new Date(response.maxPayOnDate),
      isEligible: response.isEligible ?? true,
      eligibilityErrors: response.eligibilityErrors ?? [],
    };
  }

  private transformToPaymentProfile(profile: PaymentProfileApiResponse): PaymentProfile {
    return {
      profileId: profile.profileId,
      lastFourAccountNumber: profile.lastFourAccountNumber,
      accountType: normalizeAccountType(profile.accountType),
      accountProfile: normalizeAccountProfile(profile.accountProfile),
      bankName: profile.bankName,
      isDefault: profile.defaultPaymentProfile ?? false,
      firstName: profile.firstName,
      lastName: profile.lastName,
      businessAccountName: profile.businessAccountName,
      nickName: profile.profileName,
      routingNumber: profile.routingNumber,
    };
  }

  private transformToPendingPayment(response: PendingPaymentApiResponse, paymentId: string): PendingPayment {
    return {
      paymentId: response.paymentId ?? paymentId,
      loanNumber: response.loanNumber,
      confirmationNumber: response.confirmationNumber,
      paymentDate: response.paymentDate,
      paymentStatus: response.paymentStatus?.toString() as PendingPayment['paymentStatus'],
      totalDraftAmount: response.totalDraftAmount,
      monthlyPaymentAmount: response.monthlyPaymentAmount,
      paymentDue: (response.paymentDue || []).map((due) => ({
        dueDate: due.dueDate,
        totalMonthlyPayment: due.totalMonthlyPayment,
        principalAndInterest: due.principalAndInterest,
        taxAndInsurance: due.taxAndInsurance,
        isSelected: true, // Already selected since it's a pending payment
      })),
      fees: (response.fees || []).map((fee) => ({
        type: this.toPaymentFeeType(fee.type),
        amount: fee.amount,
        description: fee.description,
        isSelected: true,
        breakdown: fee.breakdown ?? null,
      })),
      additionalPrincipal: response.additionalPrincipal ?? 0,
      additionalEscrow: response.additionalEscrow ?? 0,
      paymentProfileId: response.paymentProfileId,
      paymentProfileDetails: {
        firstName: response.paymentProfileDetails?.firstName ?? '',
        lastName: response.paymentProfileDetails?.lastName ?? '',
        businessAccountName: response.paymentProfileDetails?.businessAccountName,
        accountProfile: response.paymentProfileDetails?.accountProfile as 'Personal' | 'Business',
        accountType: response.paymentProfileDetails?.accountType as 'Checking' | 'Savings',
        routingNumber: response.paymentProfileDetails?.routingNumber ?? '',
        accountNumber: response.paymentProfileDetails?.accountNumber ?? '',
      },
      createdAt: response.createdAt,
      userName: response.userName,
      userEmail: response.userEmail,
      channelId: response.channelId,
      suspenseAmount: response.suspenseAmount,
      delinquencyReasonCode: response.delinquency?.reasonCode,
    };
  }

  private transformToAutopayRecord(response: PendingPaymentApiResponse): AutopayPaymentRecord {
    return {
      paymentId: response.paymentId ?? '',
      loanNumber: response.loanNumber,
      confirmationNumber: response.confirmationNumber,
      paymentFrequency: (response.paymentFrequency ?? 1) as AutopayPaymentRecord['paymentFrequency'],
      totalDraftAmount: response.totalDraftAmount,
      monthlyPaymentAmount: response.monthlyPaymentAmount,
      recPaymentStartDate: response.recPaymentStartDate ?? response.paymentDate,
      paymentProfileId: response.paymentProfileId,
      paymentProfileDetails: {
        firstName: response.paymentProfileDetails?.firstName,
        lastName: response.paymentProfileDetails?.lastName,
        businessAccountName: response.paymentProfileDetails?.businessAccountName,
        accountProfile: (response.paymentProfileDetails?.accountProfile ?? 'Personal') as 'Personal' | 'Business',
        accountType: (response.paymentProfileDetails?.accountType ?? 'Checking') as 'Checking' | 'Savings',
        routingNumber: response.paymentProfileDetails?.routingNumber ?? '',
        accountNumber: response.paymentProfileDetails?.accountNumber ?? '',
      },
      paymentStatus: (response.paymentStatus?.toString() ?? 'Pending') as AutopayPaymentRecord['paymentStatus'],
      additionalPrincipal: response.additionalPrincipal ?? 0,
      semiMonthlyAdditionalPrincipal: response.semiMonthlyAdditionalPrincipal ?? 0,
      additionalEscrow: response.additionalEscrow ?? 0,
      createdAt: response.recPaymentSetUpDate ?? response.createdAt,
      paymentCancellationCutoffDate: response.paymentCancellationCutoffDate,
      userName: response.userName,
      userEmail: response.userEmail,
    };
  }

  private feeTypeToNumber(type: string): number {
    const feeTypeMap: Record<string, number> = {
      LateFee: 1,
      NSFFee: 2,
      OtherFees: 3,
    };
    return feeTypeMap[type] ?? 0;
  }

  private feeTypeToCode(type: string): string {
    if (type === 'OtherFees') return '';
    return String(this.feeTypeToNumber(type));
  }

  private toPaymentFeeType(type: string | number): PaymentFeeSelection['type'] {
    const knownTypes = ['LateFee', 'NSFFee', 'OtherFees', 'AnnualFees', 'AnnualMainFee'];
    if (typeof type === 'string' && knownTypes.includes(type)) {
      return type as PaymentFeeSelection['type'];
    }
    const numType = typeof type === 'number' ? type : parseInt(String(type), 10);
    const numericMap: Record<number, PaymentFeeSelection['type']> = {
      1: 'LateFee',
      2: 'NSFFee',
      3: 'OtherFees',
      23: 'AnnualMainFee',
    };
    return numericMap[numType] ?? 'OtherFees';
  }

  private normalizeVerificationStatus(status: string): 'Valid' | 'Invalid' | 'Error' {
    const normalized = status?.toLowerCase();
    if (normalized === 'valid') return 'Valid';
    if (normalized === 'invalid') return 'Invalid';
    return 'Error';
  }
}

interface DeletedPaymentApiResponse {
  paymentId: string;
  loanNumber?: string;
  paymentFrequency?: number;
  paymentStatus?: string;
  updatedAt?: string;
  deletedAt?: string;
  deletedReason?: string;
}

interface PaymentApiResponse {
  nextDueDate: string;
  paymentsMadeThisMonth?: boolean;
  unpaidPrincipal?: number;
  principalPaid?: number;
  escrowBalance?: number;
  paymentsDue?: PaymentDueApiResponse[];
  fees?: PaymentFeeApiResponse[];
  profiles?: PaymentProfileApiResponse[];
  minPayOnDate: string;
  maxPayOnDate: string;
  isEligible?: boolean;
  eligibilityErrors?: string[];
}

interface PaymentDueApiResponse {
  dueDate: string;
  totalMonthlyPayment: number;
  principalAndInterest: number;
  taxAndInsurance: number;
  isSuspense?: boolean;
}

interface PaymentFeeApiResponse {
  type: string | number;
  amount: number;
  description?: string;
  breakdown?: { type: number; code: string; description: string; amount: number }[] | null;
}

interface PaymentProfileApiResponse {
  profileId: string;
  lastFourAccountNumber: string;
  accountType: string | number;
  accountProfile: string | number;
  bankName?: string;
  defaultPaymentProfile?: boolean;
  firstName?: string;
  lastName?: string;
  businessAccountName?: string;
  profileName?: string;
  routingNumber?: string;
}

interface PaymentConfirmationResponse {
  confirmationNumber: string;
  paymentId: string;
  paymentDate: string;
  amount: number;
  isSuccess: boolean;
  errorMessage?: string;
}

interface PendingPaymentApiResponse {
  paymentId?: string;
  loanNumber: string;
  confirmationNumber: string;
  paymentDate: string;
  paymentStatus?: string | number;
  totalDraftAmount: number;
  monthlyPaymentAmount: number;
  paymentDue?: PaymentDueApiResponse[];
  fees?: PaymentFeeApiResponse[];
  additionalPrincipal?: number;
  semiMonthlyAdditionalPrincipal?: number;
  additionalEscrow?: number;
  paymentProfileId: string;
  paymentProfileDetails?: PaymentProfileDetailsApiResponse;
  createdAt: string;
  userName?: string;
  userEmail?: string;
  paymentFrequency?: number;
  recPaymentStartDate?: string;
  recPaymentSetUpDate?: string;
  paymentCancellationCutoffDate?: string;
  channelId?: number;
  suspenseAmount?: number;
  delinquency?: {
    contactCode: string;
    responseCode: string;
    reasonCode: string;
  } | null;
}

interface PaymentProfileDetailsApiResponse {
  firstName?: string;
  lastName?: string;
  businessAccountName?: string;
  accountProfile?: string;
  accountType?: string;
  routingNumber?: string;
  accountNumber?: string;
}

interface AddBankAccountApiResponse {
  paymentProfileId: string;
  accountVerificationStatus: string;
  accountValidationResult?: {
    valid: boolean;
    validationResultMessage?: string;
  };
  bankName?: string;
  errorMessage?: string;
}

interface RoutingNumberApiResponse {
  valid: number;
  routingNumber: string;
  bankName?: string;
  error?: string;
}

interface AutopayApiResponse {
  paymentId: string;
  confirmationNumber: string;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentInfoService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get baseUrl(): string {
    return `${this.env.appSettings.ServicingLINCPortalPayments}/${this.env.appSettings.apiNamespace}/ServicingLINCPortalIntegration/Payments`;
  }

  private retryWithBackoff<T>(context: string) {
    return retry<T>({
      count: PAYMENT_RETRY_CONFIG.maxRetries,
      delay: (error: unknown, retryCount: number) => {
        const delayMs = Math.min(
          PAYMENT_RETRY_CONFIG.initialDelayMs * Math.pow(2, retryCount - 1),
          PAYMENT_RETRY_CONFIG.maxDelayMs
        );
        this.loggingService.logWarning(
          `${context} failed, retrying (${retryCount}/${PAYMENT_RETRY_CONFIG.maxRetries})`,
          {
            retryCount,
            delayMs,
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
          }
        );
        return timer(delayMs);
      },
    });
  }

  async getPaymentSummary(request: PaymentSummaryRequest): Promise<PaymentSummaryResponse> {
    const url = `${this.baseUrl}/getPaymentSummary`;
    const params = { encryptionValue: request.encryptionValue };
    const body = { loanNumber: request.loanNumber };

    return firstValueFrom(this.http.post<PaymentSummaryResponse>(url, body, { params }));
  }

  async getTaxAndInsuranceDetails(request: TaxAndInsuranceRequest): Promise<TaxAndInsuranceDetails> {
    const url = `${this.baseUrl}/getTaxAndInusranceDetails`;
    const params = { encryptionValue: request.encryptionValue };
    const body = { loanNumber: request.loanNumber };

    return firstValueFrom(this.http.post<TaxAndInsuranceDetails>(url, body, { params }));
  }

  async getPaymentActivity(request: PaymentActivityRequest): Promise<PaymentActivityResponse> {
    const url = `${this.baseUrl}/getPaymentActivity`;
    const params = { encryptionValue: request.encryptionValue };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { encryptionValue: _encryptionValue, ...body } = request;

    return firstValueFrom(this.http.post<PaymentActivityResponse>(url, body, { params }));
  }

  async getPaymentsByStatus(
    loanNumber: string,
    paymentStatus: string,
    encryptionValue: string,
  ): Promise<PaymentsByStatusResponse> {
    const url = `${this.baseUrl}/${loanNumber}/PaymentStatus/${paymentStatus}`;
    const params = { encryptionValue };

    return firstValueFrom(
      this.http.get<PaymentsByStatusResponse>(url, { params }).pipe(this.retryWithBackoff('Payments by status request'))
    );
  }
}
